import { api } from "../../api";

function toArrayResult(draft) {
  if (Array.isArray(draft?.data)) return draft.data;
  if (Array.isArray(draft)) return draft;
  return null;
}

function toObjectResult(draft) {
  if (draft?.data && typeof draft.data === "object") return draft.data;
  if (draft && typeof draft === "object") return draft;
  return null;
}

function getConversationTimestamp(conversation) {
  const value =
    conversation?.lastMessageAt ||
    conversation?.dateUpdated ||
    conversation?.dateCreated ||
    null;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function sortConversations(list) {
  list.sort((left, right) => {
    return getConversationTimestamp(right) - getConversationTimestamp(left);
  });
}

function conversationMatchesListArgs(conversation, args = {}) {
  if (!conversation) return false;
  if (args?.contextEntityType) {
    if (String(conversation?.context?.entityType) !== String(args.contextEntityType)) {
      return false;
    }
  }
  if (args?.contextEntityId) {
    if (String(conversation?.context?.entityId) !== String(args.contextEntityId)) {
      return false;
    }
  }
  return true;
}

function mergeConversation(target, patch) {
  if (!target || !patch || typeof patch !== "object") return;
  Object.assign(target, patch);
}

function patchConversationListEntry(list, conversation) {
  if (!Array.isArray(list) || !conversation?._id) return;

  const existing = list.find(
    (item) => String(item?._id) === String(conversation?._id),
  );

  if (existing) {
    mergeConversation(existing, conversation);
    sortConversations(list);
    return;
  }

  list.unshift(conversation);
  sortConversations(list);
}

function patchSingleConversation(target, conversationId, conversationPatch) {
  if (!target || String(target?._id) !== String(conversationId)) return;
  mergeConversation(target, conversationPatch);
}

function patchMessagesList(list, message) {
  if (!Array.isArray(list) || !message?._id) return;

  const existing = list.find((item) => String(item?._id) === String(message._id));
  if (existing) {
    Object.assign(existing, message);
  } else {
    list.push(message);
  }

  list.sort((left, right) => {
    const leftTime = new Date(
      left?.dateCreated || left?.createdAt || left?.timestamp || 0,
    ).getTime();
    const rightTime = new Date(
      right?.dateCreated || right?.createdAt || right?.timestamp || 0,
    ).getTime();
    return leftTime - rightTime;
  });
}

export function patchChatCaches({
  dispatch,
  getState,
  conversationId,
  conversation,
  message,
}) {
  const resolvedConversationId = String(
    conversationId || conversation?._id || message?.conversationId || "",
  );

  if (!resolvedConversationId) return;

  const conversationPatch =
    conversation && typeof conversation === "object"
      ? conversation
      : { _id: resolvedConversationId };

  const affected = api.util.selectInvalidatedBy(getState(), [
    { type: "Chat", id: resolvedConversationId },
    { type: "Chat", id: "LIST" },
    { type: "ChatMessages", id: resolvedConversationId },
  ]);

  affected.forEach(({ endpointName, originalArgs }) => {
    if (endpointName === "getChatConversations") {
      dispatch(
        api.util.updateQueryData(endpointName, originalArgs, (draft) => {
          const list = toArrayResult(draft);
          if (!list) return;
          if (!conversationMatchesListArgs(conversationPatch, originalArgs || {})) return;
          patchConversationListEntry(list, conversationPatch);
        }),
      );
      return;
    }

    if (endpointName === "getConversationById") {
      dispatch(
        api.util.updateQueryData(endpointName, originalArgs, (draft) => {
          const target = toObjectResult(draft);
          patchSingleConversation(target, resolvedConversationId, conversationPatch);
        }),
      );
      return;
    }

    if (
      endpointName === "getConversationMessages" &&
      String(originalArgs?.conversationId) === resolvedConversationId &&
      message
    ) {
      dispatch(
        api.util.updateQueryData(endpointName, originalArgs, (draft) => {
          const list = toArrayResult(draft);
          if (!list) return;
          patchMessagesList(list, message);
        }),
      );
    }
  });
}
